import type { ReactNode } from 'react';

import { NetworkError } from '../api/problem';
import { EmptyState, ErrorState, LoadingState, OfflineState } from './states/StateViews';
import { useOnlineStatus } from './useOnlineStatus';

/**
 * Renders one of the four data states, or the data.
 *
 * The order of the checks is the interesting part. Offline is tested before error, because a failed
 * fetch on a dropped connection is not a fault the reader can act on and should not be reported as
 * one — a receptionist whose clinic Wi-Fi has gone should be told exactly that, not handed a
 * request id to quote at support (NFR-010).
 */
export function AsyncSection<T>({
  query,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyAction,
  skeletonRows,
  children,
}: {
  readonly query: {
    readonly data: T | undefined;
    readonly isPending: boolean;
    readonly error: unknown;
    readonly refetch: () => unknown;
  };
  readonly isEmpty?: (data: T) => boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly emptyAction?: ReactNode;
  /**
   * How many rows are coming, roughly.
   *
   * Supplied by a section whose shape is known — a table, a list of appointments — so the wait is
   * a skeleton of that shape rather than a spinner in the middle of an empty page. It holds the
   * layout still, so nothing jumps when the data lands, and it says "a table is arriving" rather
   * than "something is happening". Omitted where the shape genuinely is not known.
   */
  readonly skeletonRows?: number;
  readonly children: (data: T) => ReactNode;
}) {
  const online = useOnlineStatus();
  const retry = () => {
    void query.refetch();
  };

  if (query.isPending) {
    return <LoadingState rows={skeletonRows} />;
  }
  if (query.error) {
    if (!online || query.error instanceof NetworkError) {
      return <OfflineState onRetry={retry} />;
    }
    return <ErrorState error={query.error} onRetry={retry} />;
  }
  if (query.data === undefined) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }
  if (isEmpty?.(query.data)) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }
  return <>{children(query.data)}</>;
}
